import { useCallback, useEffect, useState } from "react";
import {
  extractYoutubeVideoId,
  getYoutubeThumbnailUrl,
} from "../../utils/youtubePreview.util";

const ANCHOR_SELECTOR = "a";
const HREF_ATTRIBUTE = "href";
const MOUSE_OVER_EVENT = "mouseover";
const MOUSE_OUT_EVENT = "mouseout";
const SCROLL_EVENT = "scroll";
const PREVIEW_WIDTH_PX = 240;
const PREVIEW_HEIGHT_PX = 135;
const HOVER_OFFSET_PX = 12;
const VIEWPORT_PADDING_PX = 16;
const INITIAL_PREVIEW_STATE = {
  isVisible: false,
  position: { left: 0, top: 0 },
  thumbnailUrl: "",
};

const getEditorRoot = (quillRef) =>
  quillRef.current?.getEditor?.().root ?? null;

const getAnchorFromTarget = ({ editorRoot, target }) => {
  if (!(target instanceof Element)) return null;

  const anchorElement = target.closest(ANCHOR_SELECTOR);
  if (!anchorElement || !editorRoot.contains(anchorElement)) return null;

  return anchorElement;
};

const getAnchorUrl = (anchorElement) =>
  anchorElement.getAttribute(HREF_ATTRIBUTE) ?? anchorElement.href;

const calculateClampedCoordinate = ({
  pointerCoordinate,
  previewSize,
  viewportSize,
}) => {
  const preferredCoordinate = pointerCoordinate + HOVER_OFFSET_PX;
  const maxCoordinate = viewportSize - previewSize - VIEWPORT_PADDING_PX;

  return Math.max(
    VIEWPORT_PADDING_PX,
    Math.min(preferredCoordinate, maxCoordinate)
  );
};

const calculatePreviewPosition = ({ clientX, clientY }) => ({
  left: calculateClampedCoordinate({
    pointerCoordinate: clientX,
    previewSize: PREVIEW_WIDTH_PX,
    viewportSize: window.innerWidth,
  }),
  top: calculateClampedCoordinate({
    pointerCoordinate: clientY,
    previewSize: PREVIEW_HEIGHT_PX,
    viewportSize: window.innerHeight,
  }),
});

const isMouseLeavingAnchor = ({ anchorElement, relatedTarget }) => {
  if (!(relatedTarget instanceof Node)) return true;

  return !anchorElement.contains(relatedTarget);
};

const buildPreviewState = ({ clientX, clientY, videoId }) => ({
  isVisible: true,
  position: calculatePreviewPosition({ clientX, clientY }),
  thumbnailUrl: getYoutubeThumbnailUrl(videoId),
});

const createHoverHandlers = ({ editorRoot, hidePreview, showPreview }) => {
  const handleMouseOver = (event) => {
    const anchorElement = getAnchorFromTarget({
      editorRoot,
      target: event.target,
    });
    if (!anchorElement) return;

    showPreview({
      anchorElement,
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };
  const handleMouseOut = (event) => {
    const anchorElement = getAnchorFromTarget({
      editorRoot,
      target: event.target,
    });
    if (!anchorElement) return;

    const isLeaving = isMouseLeavingAnchor({
      anchorElement,
      relatedTarget: event.relatedTarget,
    });
    if (isLeaving) hidePreview();
  };

  return { handleMouseOut, handleMouseOver };
};

const attachHoverListeners = ({ editorRoot, handlers, hidePreview }) => {
  const { handleMouseOut, handleMouseOver } = handlers;

  editorRoot.addEventListener(MOUSE_OVER_EVENT, handleMouseOver);
  editorRoot.addEventListener(MOUSE_OUT_EVENT, handleMouseOut);
  editorRoot.addEventListener(SCROLL_EVENT, hidePreview);

  return () => {
    editorRoot.removeEventListener(MOUSE_OVER_EVENT, handleMouseOver);
    editorRoot.removeEventListener(MOUSE_OUT_EVENT, handleMouseOut);
    editorRoot.removeEventListener(SCROLL_EVENT, hidePreview);
  };
};

const useYoutubeThumbnailHover = (quillRef) => {
  const [previewState, setPreviewState] = useState(INITIAL_PREVIEW_STATE);
  const hidePreview = useCallback(() => {
    setPreviewState(INITIAL_PREVIEW_STATE);
  }, []);
  const showPreview = useCallback(({ anchorElement, clientX, clientY }) => {
    const videoId = extractYoutubeVideoId(getAnchorUrl(anchorElement));
    if (!videoId) return;

    setPreviewState(buildPreviewState({ clientX, clientY, videoId }));
  }, []);

  useEffect(() => {
    const editorRoot = getEditorRoot(quillRef);
    if (!editorRoot) return undefined;

    const handlers = createHoverHandlers({
      editorRoot,
      hidePreview,
      showPreview,
    });

    return attachHoverListeners({ editorRoot, handlers, hidePreview });
  }, [hidePreview, quillRef, showPreview]);

  return { hidePreview, previewState };
};

export default useYoutubeThumbnailHover;
